import React from "react";
import styled from "styled-components";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button, Checkbox_, Filled_Edit, FormBox, Grey_Link, IconSvg, Input_, Main_Plain, Partial_Loader, Red_, device,} from "monica-alexandria";
import Crm_Total from "./Crm_Total";
import { useTranslation } from "react-i18next";
import { formatCallFollowUp, readCallFollowUpState } from "../callFollowUp";

const CrmBoard = styled.div`
  display: flex;
  gap: 1rem;
  min-width: 100%;
  height: calc(100vh - 18.5rem);
  padding-bottom: 2rem;
  overflow-x: auto;
  scroll-behavior: smooth;
  white-space: nowrap;
`;

const CrmColumn = styled.div`
  background: ${(p) => p.bg || p.theme.mid};
  position: relative;
  flex: 1;
  min-width: 30rem;
  padding: var(--normalPads);
  border-radius: var(--smallRadius);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  transition: background 0.3s ease;
  padding-bottom: 6.5rem;

  &:hover .add-lead {
    opacity: 1;
    pointer-events: all;
    transform: translateY(0);
  }

  &:first-of-type {
    margin-left: 4rem;
  }

  @media ${device.sm} {
    &:first-of-type {
      margin-left: 2rem;
    }
  }
`;

const ColumnHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  gap: 0.5rem;

  position: sticky;
  top: 0;
  z-index: 10;

  h4 {
    cursor: pointer;
    flex: 1;
  }
`;

const ColumnTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex: 1;
  min-width: 0;

  h4 {
    flex: 0 1 auto;
    min-width: 0;
  }
`;

const CrmCard = styled(FormBox)`
  background: ${(p) => p.theme.low};
  padding: var(--smallPads) ;
  border-radius: var(--smallRadius);
  cursor: grab;
  transition: transform 0.1s ease, background 0.2s ease;
  border: 2px solid transparent;
  margin-bottom: 0rem;
   box-shadow: none;
  position: relative;

  ${(p) =>
    p.selectionMode &&
    `
      cursor: pointer;
    `}

  ${(p) =>
    p.selected &&
    `
      border-color: var(--main);
      background: ${p.theme.background};
    `}

  &:hover {
    background: ${(p) => p.theme.background};
    border: 1px solid ${(p) => p.theme.mid};
    box-shadow: none;
  }
  
`;

const CardCheckbox = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
`;

const CrmCardInfo = styled.div`
  p {
    opacity: 0.8;
  }
`;

const CrmCardInfoTop = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
    h5 {
    flex: 1;
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  /* gap: 1rem; */
`;
const CrmCardDate = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-top: 2rem;

  svg {
    width: 1.5rem;
    fill: ${(p) => p.theme.color};
  }

  p {
    font-size: 1.4rem;
  }
`;

const CallFollowUpBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: fit-content;
  margin-top: 1.2rem;
  padding: 0.6rem 0.9rem;
  border-radius: var(--smallRadius);
  color: ${(p) => p.state === "overdue" ? "#a62b2b" : "var(--main)"};
  background: ${(p) => p.state === "overdue" ? "rgba(198, 50, 50, 0.12)" : "rgba(45, 96, 103, 0.12)"};
  white-space: normal;

  svg { width: 1.5rem; fill: currentColor; }
  p { font-size: 1.3rem; font-weight: 600; color: inherit; }
`;

const CrmAddColumn = styled(Button)`
  border-radius: var(--smallRadius);
  background: ${(p) => p.theme.high};
  border: 2px solid ${(p) => p.theme.low};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4rem;

  svg {
    fill: ${(p) => p.theme.color};
  }

  @media ${device.sm} {
    margin-right: 2rem;
  }
`;

const ColorPicker = styled.input`
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  cursor: pointer;
`;

const ColumnMenu = styled.div`
  position: absolute;
  top: 2.5rem;
  right: 0.5rem;
  background: ${(p) => p.theme.low};
  border-radius: var(--smallRadius);
  padding: var(--smallPads);
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .disabled {
    pointer-events: none;
    opacity: 0.5;
  }
`;

const ChangeBackgroundBtn = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;

  h5 {
    cursor: pointer !important;
  }

  input[type="color"] {
    -webkit-appearance: none;
    border: 1px solid var(--grey);
    width: 20px;
    height: 20px;
    padding: 0;
    cursor: pointer;
    border-radius: 50%;
    overflow: hidden;
    background: none;
  }

  input[type="color"]::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  input[type="color"]::-webkit-color-swatch {
    border: none;
    border-radius: 50%;
  }
`;

const CrmColumnInner = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  max-height: 70vh;
  padding-right: 0.5rem;

  /* ---------- Default: hide all scrollbars ---------- */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE / Edge */

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  /* ---------- On hover: show scrollbars ---------- */
  &:hover {
    scrollbar-width: thin; /* Firefox */
  }

  &:hover::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &:hover::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
  }

  &:hover::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 10px;
  }
`;

export default function Crm_Kanban_Board(props) {
    const {t} = useTranslation();


  return (
    <DragDropContext
      onDragStart={() => {
        if (props.selectionMode) return;
        props.isDraggingCardRef.current = true;
      }}
      onDragEnd={(result) => {
        props.isDraggingCardRef.current = false;
        if (props.selectionMode) return;
        props.onDragEnd(result);
      }}
    >
      <Droppable droppableId="board" direction="horizontal" type="column">
        {(provided) => (
          <CrmBoard
            ref={(node) => {
              props.boardRef.current = node;
              provided.innerRef(node);
            }}
            {...provided.droppableProps}
          >
            {props.columnOrder.map((colId, index) => {
              const col = props.columns[colId];
              if (!col) return null;

              const totalAmount = (col.items || []).reduce((sum, lead) => {
                const val = parseFloat(lead?.Amount);
                return sum + (isNaN(val) ? 0 : val);
              }, 0);

              return (
                <Draggable key={col.id} draggableId={col.id} index={index} isDragDisabled={props.selectionMode}>
                  {(dragProvided) => (
                    <CrmColumn
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      style={{
                        ...dragProvided.draggableProps.style,
                        background: (col.color || "#ffffff") + "33",
                      }}
                    >
                      <ColumnHeader {...dragProvided.dragHandleProps}>
                        <ColumnTitle>
                          {props.editingColumnId === col.id ? (
                            <Input_
                              ref={props.inputRef}
                              type="text"
                              value={props.tempColumnTitle}
                              onChange={(e) => props.setTempColumnTitle(e.target.value)}
                              onKeyDown={props.handleColumnTitleKeyDown}
                              onBlur={props.saveColumnTitle}
                            />
                          ) : (
                            <h4 onDoubleClick={() => props.startEditingColumn(col.id, col.title)}>
                              {col.title}
                            </h4>
                          )}
                        </ColumnTitle>
                        <Grey_Link size="normal" noPadding iconLeft="More" onClick={() => props.setOpenMenu(props.openMenu === col.id ? null : col.id)} />
                      </ColumnHeader>

                      {props.openMenu === col.id && (
                        <ColumnMenu ref={props.menuRef}>
                          <Main_Plain iconLeft="Edit"  onClick={() => props.startEditingColumn(col.id, col.title)}  noPadding text={t("Rename column")} />
                          <ChangeBackgroundBtn>
                            <ColorPicker
                              type="color"
                              id="background"
                              name="background"
                              value={col.color}
                              onChange={(e) => props.changeColumnColor(col.id, e.target.value)}
                              placeholder={t("Background")}
                            />
                            <label htmlFor="background">
                              <h5>{t("Background")}</h5>
                            </label>
                          </ChangeBackgroundBtn>
                          <Red_  iconLeft="Delete" noPadding  text={t("Delete column")} onClick={() => props.setColumnDlt(col.id)}className={col.items.length !== 0 ? "disabled" : ""}/>
                        </ColumnMenu>
                      )}

                      <Droppable droppableId={col.id} type="lead">
                        {(dropProvided) => (
                          <CrmColumnInner
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                            onScroll={(e) => {
                              const el = e.target;
                              if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
                                if (props.columnHasMore[col.id] && !props.columnLoading[col.id]) {
                                  props.loadColumn(col.id);
                                }
                              }
                            }}
                          >
                            {(col.items || []).map((lead, idx) => (
                              <Draggable
                                key={String(lead._id || lead.id)}
                                draggableId={String(lead._id || lead.id)}
                                index={idx}
                                isDragDisabled={props.selectionMode}
                              >
                                {(dragLeadProvided) => (
                                  <CrmCard
                                    ref={dragLeadProvided.innerRef}
                                    {...dragLeadProvided.draggableProps}
                                    {...(props.selectionMode ? {} : dragLeadProvided.dragHandleProps)}
                                    onClick={() => {
                                      if (props.selectionMode) {
                                        props.toggleLeadSelection?.(lead._id);
                                        return;
                                      }

                                      props.openLeadEditor(col.id, lead);
                                    }}
                                    selectionMode={props.selectionMode}
                                    selected={props.selectedLeadIds?.includes(String(lead._id))}
                                    filled
                                  >
                                  {props.selectionMode ? (
                                    <CardCheckbox onClick={(e) => e.stopPropagation()}>
                                      <Checkbox_
                                        type="checkbox"
                                        checked={props.selectedLeadIds?.includes(String(lead._id))}
                                        onChange={() => props.toggleLeadSelection?.(lead._id)}
                                      />
                                    </CardCheckbox>
                                  ) : null}
                                  <CrmCardInfoTop>
                                    <Filled_Edit />
                                    <h5>
                                      {lead.FullName || (lead.Name || lead.Surname
                                      ? `${lead.Name || ""} ${lead.Surname || ""}`
                                      : t("New Lead"))}
                                    </h5>
                                  </CrmCardInfoTop>
                             
                                    <CrmCardInfo>
                                      <p>{lead.Email}</p>
                                      <p>{lead.Tel}</p>
                                    </CrmCardInfo>

                                    {readCallFollowUpState(lead) !== "none" ? (
                                      <CallFollowUpBadge state={readCallFollowUpState(lead)}>
                                        <IconSvg Icon="Phone" />
                                        <p>{formatCallFollowUp(lead, t)}</p>
                                      </CallFollowUpBadge>
                                    ) : null}

                                    <CrmCardDate>
                                      <IconSvg Icon="Calendar" />
                                      <p>
                                        {t("Created")}{" "}
                                        {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString()  : ""}
                                      </p>
                                    </CrmCardDate>
                                  </CrmCard>
                                )}
                              </Draggable>
                            ))}

                            {dropProvided.placeholder}

                            {props.columnLoading[col.id] && <Partial_Loader />}

                            {!props.columnLoading[col.id] && props.columnHasMore[col.id] && (
                              <div style={{ textAlign: "center" }}>
                                <Main_Plain text={t("Load more")} onClick={() => props.loadColumn(col.id)} noPadding />
                              </div>
                            )}
                          </CrmColumnInner>
                        )}
                      </Droppable>
                      {!props.selectionMode ? (
                        <Grey_Link size="small" noPadding iconLeft="Add" text="New lead" onClick={() =>props.openAddLead(col.id)} />
                      ) : null}
                      <Crm_Total
                        total={totalAmount || "0,0"}
                        leadCount={col.leadCount ?? (col.items || []).length}
                      />
                    </CrmColumn>
                  )}
                </Draggable>
              );
            })}

            {provided.placeholder}

            {!props.selectionMode ? (
              <CrmAddColumn title="Add column" onClick={props.handleNewColumn}>
                <IconSvg Icon="Add" />
              </CrmAddColumn>
            ) : null}
          </CrmBoard>
        )}
      </Droppable>
    </DragDropContext>
  );
}
